import React, { useEffect, useRef, useState } from "react";
import { ChevronRight, Clock, X, CircleCheck, AlertTriangle, Calendar } from "lucide-react";
import HeaderModal from "components/modal/HeaderModal";
import { Button, Divider } from "antd";
import IndexModal from "components/modal";
import AnnouncementModal from "../../employee-dashboard/components/AnnouncementModal";

const AnnouncementWidget = ({ announcements, selectedId, setSelectedId, autoInterval = 4000, userType = "client", onRemove }) => {
  const timerRef = useRef(null);

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [announcements.length, autoInterval]);

  const startTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setSelectedId((prev) => (prev + 1) % announcements.length);
    }, autoInterval);
  };

  const handleClick = (id) => {
    setSelectedId(id);
    startTimer();
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);

  const openModal = (item) => {
    setSelectedAnnouncement(item);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedAnnouncement(null);
    setIsModalOpen(false);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
      {announcements.map((item, id) => {
        const Icon = item.icon;
        const isSelected = selectedId === id;

        return (
          <div
            key={id}
            onClick={() => {
              handleClick(id);
              openModal(item);
            }}
            className={`
              group relative p-2.5 rounded-lg cursor-pointer transition-all duration-300
              bg-white dark:bg-zinc-800
              border border-slate-200 dark:border-zinc-700
              ${isSelected
                ? "border-violet-400 shadow-lg shadow-violet-300/40"
                : "hover:border-violet-300 hover:shadow-sm"
              }
            `}
          >
            <button
               onClick={(e) => {
                  e.stopPropagation();
                  onRemove(id);
                }}
              className="absolute top-1.5 right-1.5 p-0.5 rounded opacity-0 group-hover:opacity-100 bg-slate-100 dark:bg-zinc-700 hover:bg-slate-200 transition-all"
            >
              <X className="w-2.5 h-2.5 text-slate-800" />
            </button>


            <div className="flex items-start gap-2 mb-2">
              <div className={`p-1.5 rounded-lg flex-shrink-0 bg-text ${item.category.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon className="w-3 h-3" />
              </div>

              <div className="flex-1 min-w-0">
                <span className={`status-indicator ${item.priority.toLowerCase()} text-[8px] px-1 py-0 mb-1`}>
                  {item.priority}
                </span>
                <h5 className="text-[11px] leading-tight mb-0.5 dark:text-zinc-100 line-clamp-1 font-semibold">
                  {item.title}
                </h5>
              </div>
            </div>

            <p className="text-[9px] text-slate-700 dark:text-zinc-400 line-clamp-2 mb-2">
              {item.description}
            </p>

            <div className="flex items-center justify-between text-[8px] text-slate-600 dark:text-zinc-500">
              <span className="flex items-center gap-0.5">
                <Clock className="w-2.5 h-2.5" />
                {item.dateAnnounced}
              </span>
              <ChevronRight className="w-2.5 h-2.5 text-violet-600" />
            </div>
          </div>
        );
      })}

      {isModalOpen && selectedAnnouncement && (
        
        <>
          {userType === "client" && (
            <HeaderModal
          group="announcement"
          open={isModalOpen}
          onClose={closeModal}
          colorTheme="!p-0 backdrop-blur-lg"
          useCustomClass={true}
          descriptionClass="text-2xl text-white font-medium"
          description={selectedAnnouncement.title}
          width={550}
          backgroundStyle={
            <div className="absolute inset-0 pointer-events-none opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -mr-32 -mt-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full -ml-24 -mb-24"></div>
            </div>
          }
          icon={
            <div className="w-16 h-16 rounded-2xl shadow-lg bg-white/20 flex items-center justify-center">
              {selectedAnnouncement.icon && (
                <selectedAnnouncement.icon className="w-8 h-8" />
              )}
            </div>
          }
          title={
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center rounded-lg border px-2 py-0.5 font-medium text-[10px] uppercase tracking-wider bg-white/20 border-white/30 text-white">
                {selectedAnnouncement.category}
              </span>
              {selectedAnnouncement.priority === "high" && (
                <span className="flex items-center gap-1 justify-center rounded-lg px-2 py-0.5 font-medium text-[10px] bg-red-500 text-white">
                  <AlertTriangle className="w-3 h-3" /> Urgent
                </span>
              )}
              <span className="rounded-xl border backdrop-blur-sm bg-white/10 text-[10px] px-1 py-0 font-medium">
                {selectedAnnouncement.priority} priority
              </span>
            </div>
          }

          bgcolor={`group gradient ${selectedAnnouncement.category.toLowerCase().replace(/\s+/g, "-")}`}
          footer={
            <div className="p-4 inline-flex gap-2 grid grid-cols-1 md:grid-cols-3">
              <Button className="rounded-lg px-4 flex items-center justify-center gap-2" onClick={closeModal}>
                <X className="w-4 h-4" /> Dismiss
              </Button>
              <Button className="bg-violet-600 text-white rounded-lg flex items-center justify-center col-span-2 gap-2">
                <CircleCheck className="w-4 h-4" />
                Mark as Read
              </Button>
            </div>
          }
        >
          <div className="space-y-4 pt-4 ">
              <div>
                <h4 className="text-sm text-slate-700 mb-2 font-semibold">Details</h4>
                <p className="text-slate-600 text-base">
                  {selectedAnnouncement.description}
                </p>
              </div>

              <Divider/>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-zinc-800">
                  <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900/50">
                    <Calendar className="w-4 h-4 text-purple-600"/>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500  uppercase tracking-wide">Date</p>
                    <p className="text-sm text-slate-900 font-semibold">{selectedAnnouncement.dateAnnounced}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50">
                  <div className="p-2 rounded-lg bg-purple-100">
                    <Clock className="w-4 h-4 text-purple-600"/>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-500 dark:text-zinc-500 uppercase tracking-wide">Time</p>
                    <p className="text-sm text-slate-900 font-semibold">{selectedAnnouncement.time}</p>
                  </div>
                </div>
              </div>

              <div className={`p-4 rounded-lg card-border-s ${selectedAnnouncement.category.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-start gap-3">
                  <selectedAnnouncement.icon className="w-8 h-8 icon-color" />
                  <div>
                    <p className="label-color text-sm mb-1 font-semibold">{selectedAnnouncement.infoLabel}</p>
                    <p className="text-color text-xs">
                      {selectedAnnouncement.additionalInfo}
                    </p>
                  </div>
                </div>
              </div>
            </div>

      
            </HeaderModal>
          )}

          {userType === "employee" && (
            <IndexModal
              open={isModalOpen}
              onClose={closeModal}
              useCustomClass={true}
              maxContentHeight="298px"
              width="500px"
            >
              <AnnouncementModal 
                announcement={selectedAnnouncement}
                onClose={closeModal}
              />
            </IndexModal>
          )}
        </>
      )}


      

    </div>
  );
};

export default AnnouncementWidget;
